import React, { useCallback, useEffect, useRef, useState } from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

// Tiptap v3 ships React 19 typings; the admin runs React 18 : the runtime is
// compatible, only the JSX component type needs realigning.
const TiptapContent = EditorContent as unknown as React.FC<{ editor: Editor | null; className?: string }>;
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import {
  FiBold,
  FiCornerUpLeft,
  FiCornerUpRight,
  FiItalic,
  FiLink,
  FiList,
  FiMinus,
  FiSmile,
  FiUnderline,
  FiX,
} from "react-icons/fi";
import { INLINE_ICON_LABELS, INLINE_ICON_NAMES, inlineIconSvg } from "@bcc/core-renderers";
import { InlineIcon, isSafeEditorHref } from "./richTextExtensions";

export type RichTextEditorFieldProps = {
  value: string;
  onChange: (html: string) => void;
  editable?: boolean;
  minHeight?: string;
};

const TOOLBAR_BUTTON =
  "inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-sm font-semibold transition hover:bg-white hover:shadow disabled:cursor-not-allowed disabled:opacity-50";

function toolbarButtonClass(active: boolean): string {
  return `${TOOLBAR_BUTTON} ${active ? "bg-white text-[#005CA9] shadow" : "text-slate-700"}`;
}

type BlockStyle = "paragraph" | "h2" | "h3" | "h4";

function currentBlockStyle(editor: Editor | null): BlockStyle {
  if (!editor) return "paragraph";
  if (editor.isActive("heading", { level: 2 })) return "h2";
  if (editor.isActive("heading", { level: 3 })) return "h3";
  if (editor.isActive("heading", { level: 4 })) return "h4";
  return "paragraph";
}

/**
 * Full editorial rich-text editor for the RichText block body (`content.html`),
 * replacing the raw HTML textarea: headings 2-4, bold/italic/underline, lists,
 * blockquote, safe links, inline icons, horizontal rule, undo/redo. The output
 * HTML is sanitised at render time by the SDK (`sanitizeRichTextHtml` is the
 * only path it reaches the DOM).
 */
export const RichTextEditorField: React.FC<RichTextEditorFieldProps> = ({
  value,
  onChange,
  editable = true,
  minHeight = "260px",
}) => {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkValue, setLinkValue] = useState("");
  const [linkError, setLinkError] = useState(false);
  const [iconOpen, setIconOpen] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    editable,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
        code: false,
        codeBlock: false,
        strike: false,
      }),
      Underline,
      Link.configure({ openOnClick: false, autolink: false, linkOnPaste: true }),
      InlineIcon,
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose-editor w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none focus:border-slate-400 [&_h2]:mt-4 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-[#005CA9] [&_h3]:mt-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-[#005CA9] [&_h4]:mt-2 [&_h4]:text-base [&_h4]:font-semibold [&_h4]:text-[#005CA9] [&_p]:my-2 [&_a]:text-[#005CA9] [&_a]:underline [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_blockquote]:my-3 [&_blockquote]:border-l-4 [&_blockquote]:border-[#005CA9] [&_blockquote]:pl-3 [&_blockquote]:italic [&_hr]:my-4 [&_hr]:border-slate-200",
        style: `min-height: ${minHeight}`,
      },
    },
    onUpdate: ({ editor: e }) => {
      onChangeRef.current(e.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== (value ?? "")) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  const applyLink = useCallback(() => {
    if (!editor) return;
    const href = linkValue.trim();
    if (!isSafeEditorHref(href)) {
      setLinkError(true);
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
    setLinkOpen(false);
    setLinkValue("");
    setLinkError(false);
  }, [editor, linkValue]);

  const setBlockStyle = useCallback(
    (style: BlockStyle) => {
      if (!editor) return;
      if (style === "paragraph") editor.chain().focus().setParagraph().run();
      else editor.chain().focus().setHeading({ level: style === "h2" ? 2 : style === "h3" ? 3 : 4 }).run();
    },
    [editor],
  );

  return (
    <div className="flex min-h-0 flex-col">
      {editable && (
        <div className="mb-2 shrink-0 flex flex-wrap items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
          <select
            value={currentBlockStyle(editor)}
            onChange={(e) => setBlockStyle(e.target.value as BlockStyle)}
            className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 focus:border-slate-400 focus:outline-none"
            aria-label="Style du bloc"
          >
            <option value="paragraph">Paragraphe</option>
            <option value="h2">Titre 2</option>
            <option value="h3">Titre 3</option>
            <option value="h4">Titre 4</option>
          </select>
          <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden="true" />
          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleBold().run()}
            className={toolbarButtonClass(Boolean(editor?.isActive("bold")))}
            title="Gras"
            aria-label="Gras"
          >
            <FiBold className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            className={toolbarButtonClass(Boolean(editor?.isActive("italic")))}
            title="Italique"
            aria-label="Italique"
          >
            <FiItalic className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
            className={toolbarButtonClass(Boolean(editor?.isActive("underline")))}
            title="Souligné"
            aria-label="Souligné"
          >
            <FiUnderline className="h-4 w-4" />
          </button>
          <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden="true" />
          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            className={toolbarButtonClass(Boolean(editor?.isActive("bulletList")))}
            title="Liste à puces"
            aria-label="Liste à puces"
          >
            <FiList className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            className={toolbarButtonClass(Boolean(editor?.isActive("orderedList")))}
            title="Liste numérotée"
            aria-label="Liste numérotée"
          >
            <span className="text-xs font-bold">1.</span>
          </button>
          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleBlockquote().run()}
            className={toolbarButtonClass(Boolean(editor?.isActive("blockquote")))}
            title="Citation"
            aria-label="Citation"
          >
            <span className="text-base font-bold leading-none">«»</span>
          </button>
          <button
            type="button"
            onClick={() => editor?.chain().focus().setHorizontalRule().run()}
            className={`${TOOLBAR_BUTTON} text-slate-700`}
            title="Ligne horizontale"
            aria-label="Ligne horizontale"
          >
            <FiMinus className="h-4 w-4" />
          </button>
          <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden="true" />
          <button
            type="button"
            onClick={() => {
              setIconOpen(false);
              setLinkValue((editor?.getAttributes("link").href as string) ?? "");
              setLinkError(false);
              setLinkOpen((open) => !open);
            }}
            className={toolbarButtonClass(Boolean(editor?.isActive("link")) || linkOpen)}
            title="Lien"
            aria-label="Lien"
          >
            <FiLink className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              setLinkOpen(false);
              setIconOpen((open) => !open);
            }}
            className={toolbarButtonClass(iconOpen)}
            title="Insérer une icône"
            aria-label="Insérer une icône"
          >
            <FiSmile className="h-4 w-4" />
          </button>
          <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden="true" />
          <button
            type="button"
            onClick={() => editor?.chain().focus().undo().run()}
            disabled={!editor?.can().undo()}
            className={`${TOOLBAR_BUTTON} text-slate-600`}
            title="Annuler"
            aria-label="Annuler"
          >
            <FiCornerUpLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => editor?.chain().focus().redo().run()}
            disabled={!editor?.can().redo()}
            className={`${TOOLBAR_BUTTON} text-slate-600`}
            title="Rétablir"
            aria-label="Rétablir"
          >
            <FiCornerUpRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {editable && linkOpen && (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-2">
          <input
            type="text"
            value={linkValue}
            onChange={(e) => {
              setLinkValue(e.target.value);
              setLinkError(false);
            }}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyLink())}
            placeholder="https://… ou /chemin-interne"
            className={`min-w-0 flex-1 rounded-md border px-2.5 py-1.5 text-sm outline-none focus:border-slate-400 ${
              linkError ? "border-rose-300 bg-rose-50" : "border-slate-200"
            }`}
            aria-label="Adresse du lien"
          />
          <button
            type="button"
            onClick={applyLink}
            className="rounded-md bg-[#005CA9] px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-[#0F2849]"
          >
            Appliquer
          </button>
          {editor?.isActive("link") && (
            <button
              type="button"
              onClick={() => {
                editor?.chain().focus().extendMarkRange("link").unsetLink().run();
                setLinkOpen(false);
              }}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
            >
              <FiX className="h-4 w-4" />
              Retirer le lien
            </button>
          )}
          {linkError && <p className="w-full text-xs text-rose-600">Adresse invalide ou non autorisée.</p>}
        </div>
      )}

      {editable && iconOpen && (
        <div className="mb-2 grid grid-cols-6 gap-1 rounded-lg border border-slate-200 bg-white p-2 sm:grid-cols-12">
          {INLINE_ICON_NAMES.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => {
                editor?.chain().focus().insertContent({ type: "inlineIcon", attrs: { name } }).run();
                setIconOpen(false);
              }}
              title={INLINE_ICON_LABELS[name] ?? name}
              aria-label={INLINE_ICON_LABELS[name] ?? name}
              className="flex h-9 items-center justify-center rounded-md text-[#005CA9] transition hover:bg-slate-100"
              // Trusted static SVG from the SDK catalogue : never user content.
              dangerouslySetInnerHTML={{ __html: inlineIconSvg(name) }}
            />
          ))}
        </div>
      )}

      <TiptapContent editor={editor as Editor | null} className="min-h-0 flex-1" />
    </div>
  );
};
